<template>
  <StepLayout
    :back="back"
    :next="next"
    buttonDataEc="envelopes_summary_page"
    class="relative"
  >
    <template v-slot:title>
      {{
        isSent
          ? $t('ui.envelopes.create.steps.summary.sentTitle')
          : $t('ui.envelopes.create.steps.summary.title')
      }}
    </template>

    <LoadingSpinner v-if="isFetchingEnvelopeProgress" />

    <div class="space-y-base text-black">
      <EnvelopeNameSummaryItem
        :envelope="envelope"
        :envelopeSent="isSent"
        @editBeingMade="(value) => editBeingMade = value"
      />
      <hr aria-hidden="true" />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.documents.title')"
        :to="toEditStep('EnvelopesStepDocuments')"
        :disabled="isDisabled"
        dataEc="envelopes_summary_page"
        dataEt="go_to_step_documents_button"
      >
        {{ documentNames }}
      </SummaryItem>
      <hr aria-hidden="true" />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.parties.title')"
        :to="toEditStep('EnvelopesStepParties')"
        :disabled="isDisabled"
        dataEc="envelopes_summary_page"
        dataEt="go_to_step_parties_button"
        controlClasses="absolute md:static top-0 right-0"
      >
        <div class="flex flex-col gap-base w-full mt-lg md:mt-0">
          <PartySummaryCard
            v-for="party in parties"
            :party="party"
            :key="party.id"
          />
        </div>
      </SummaryItem>
      <hr aria-hidden="true" />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.fields.title')"
        :to="toEditStep('EnvelopesStepFields')"
        :disabled="isDisabled"
        dataEc="envelopes_summary_page"
        dataEt="go_to_step_fields_button"
      >
        {{
          $tc('ui.envelopes.create.steps.summary.fields.description', numberOfFields, {
            fields: numberOfFields,
          })
        }}
      </SummaryItem>
      <hr aria-hidden="true" />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.signingSequence.title')"
        :to="toEdit('EnvelopesEditSigningSequence')"
        :disabled="isDisabled"
        dataEc="envelopes_summary_page"
        dataEt="edit_signing_sequence_button"
      >
        {{ $t(`ui.envelopes.create.steps.summary.signingSequence.${signingSequence}`) }}
      </SummaryItem>
      <hr aria-hidden="true" />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.sharing.title')"
        :to="toEdit('EnvelopesEditSharing')"
        :disabled="isDisabled"
        dataEc="envelopes_summary_page"
        dataEt="edit_sharing_button"
      >
        {{
          $t(`ui.envelopes.create.steps.summary.sharing.${sharingType}`, {
            companyName: company.name,
          })
        }}
      </SummaryItem>
      <hr aria-hidden="true" />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.reminders.title')"
        :to="toEdit('EnvelopesEditReminders')"
        :disabled="isDisabled"
        dataEc="envelopes_summary_page"
        dataEt="edit_reminders_button"
      >
        {{
          $tc(
            'ui.envelopes.create.steps.summary.reminders.description',
            reminderInterval,
            { interval: reminderInterval },
          )
        }}
      </SummaryItem>
      <hr aria-hidden="true" />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.expires.title')"
        :to="toEdit('EnvelopesEditExpiry')"
        :disabled="isDisabled"
        dataEc="envelopes_summary_page"
        dataEt="edit_expiry_button"
      >
        {{ expiresAt }}
      </SummaryItem>
      <hr aria-hidden="true" />

      <SummaryItem
        :title="$t('ui.envelopes.create.steps.summary.preview.title')"
        dataEc="envelopes_summary_page"
        dataEt="launch_preview_button"
      >
        <template v-slot:controls>
          <ButtonLink
            :to="previewUrl"
            target="_blank"
            variant="secondary"
            class="whitespace-nowrap"
            :disabled="isDisabled"
            dataEc="envelopes_summary_page"
            dataEt="launch_preview_button"
            v-show="previewUrl"
          >
            {{ $t('functional.launchPreview') }}
            <Icon
              name="external-link-alt"
              class="ml-base"
            />
          </ButtonLink>
        </template>
      </SummaryItem>
    </div>

    <InfoMessage
      v-if="Object.keys(usage).length && !usage.usage_allowed"
      class="mt-lg"
    >
      <template v-slot:text>
        {{ $t('bulkSend.steps.usage.insufficientEnvelopes') }}
        <span v-if="!userHasRole('super-admin')">{{ $t('bulkSend.steps.usage.popoverContactAdminUpgrade') }}</span>
      </template>
      <template v-slot:action>
        <ButtonLink
          v-if="userHasRole('super-admin')"
          variant="secondary"
          :to="{ name: 'Billing', params: { redirectBackOnPlanSwitch: true } }"
          data-ec="envelopes_summary_page"
          data-et="go_to_billing_button"
        >
          {{ $t('functional.goToBillingSettings') }}
        </ButtonLink>
      </template>
    </InfoMessage>

    <OutdatedDraftModal
      :updateError="sendErrorEnvelope"
      @close="resetSendErrorEnvelope"
      @reload="reload"
    />
  </StepLayout>
</template>

<script setup>
// @ts-check
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import { useGetters, useActions } from 'vuex-composition-helpers/dist';

import { longDate } from '@/utils/dates';
import { EventBus } from '@/utils/event-bus';
import {
  useEnvelope,
  useEnvelopeDocumentSummaries,
  useEnvelopeParties,
  useEnvelopeProgress,
  useEnvelopeSend,
  useEnvelopeSharing,
  useEnvelopesRoutes,
} from '@/composables/envelopes';
import { useUserCurrentPreviewKey } from '@/composables/user';
import i18n from '@/locales';

import Icon from '@/components/Icon.vue';
import StepLayout from '@/components/layouts/StepLayout';
import ButtonLink from '@/components/library/links/ButtonLink.vue';
import InfoMessage from '@/components/library/message/InfoMessage';
import SummaryItem from '@/components/library/summary/SummaryItem';
import LoadingSpinner from '@/components/tailwind/LoadingSpinner.vue';
import { OutdatedDraftModal } from '@/components/ui/envelopeCreate/OutdatedDraftModal';
import { PartySummaryCard } from '@/components/ui/envelopeCreate/steps/summary/PartySummaryCard';
import EnvelopeNameSummaryItem from '@/components/ui/envelopeCreate/EnvelopeNameSummaryItem.vue';

const signingPageUrl = process.env.VUE_APP_SIGNING_PAGE;

const route = useRoute();
const router = useRouter();

const { prevRoute } = useEnvelopesRoutes();

const editBeingMade = ref(false);

const { envelope } = useEnvelope(route.params.fingerprint);
const {
  envelopeSent,
  isPendingEnvelopeProgress,
  isFetchingEnvelopeProgress,
  refetchEnvelopeProgress,
} = useEnvelopeProgress(route.params.fingerprint);
const { documentSummaries } = useEnvelopeDocumentSummaries(route.params.fingerprint);
const { parties } = useEnvelopeParties(route.params.fingerprint);
const { sharing } = useEnvelopeSharing(route.params.fingerprint);
const { previewKey } = useUserCurrentPreviewKey();

const {
 company, authenticatedUser, usage, userHasRole,
} = useGetters({
  company: 'company/company',
  authenticatedUser: 'user/user',
  usage: 'metering/usage',
  userHasRole: 'roles/userHasRole',
});

const { getUsage } = useActions({
  getUsage: 'metering/GET_USAGE',
});

// Fetch the usage for the company if they are on new billing
if (company.value.new_billing_enabled) {
  getUsage();
}

const isSent = computed(() => envelopeSent.value || isPendingEnvelopeProgress.value);
const isDisabled = computed(() => isSent.value || editBeingMade.value);

const documentNames = computed(() =>
  documentSummaries.value.map(document => document.title).join(', '));

const numberOfFields = computed(() => {
  let totalFields = 0;

  documentSummaries.value.forEach((document) => {
    totalFields += document.fields;
  });

  return totalFields;
});

const signingSequence = computed(() =>
  (envelope.value?.parallel_signing ? 'parallel' : 'sequential'));

const sharingType = computed(() => {
  if (sharing.value?.everyone_permission) {
    return 'everyone';
  }

  if (
    sharing.value?.groups.length === 0 &&
    sharing.value?.users.length === 1 &&
    sharing.value?.users[0].user.id === authenticatedUser.value.id
  ) {
    return 'private';
  }

  return 'individuals';
});

const expiresAt = computed(() => {
  if (!envelope.value?.next_expire) {
    return i18n.t('ui.envelopes.create.steps.summary.expires.noneSet');
  }
  return longDate(envelope.value?.next_expire);
});

const reminderInterval = computed(() => Math.floor((envelope.value?.auto_remind_hours || 0) / 24));

const previewUrl = computed(() => {
  if (parties.value?.length && previewKey.value) {
    return `${signingPageUrl}/preview/${route.params.fingerprint}/${parties.value[0].status.fingerprint}#${previewKey.value?.preview_key}`;
  }

  return '';
});

/** @param {`EnvelopesEdit${string}`} page */
function toEdit(page) {
  return {
    name: page,
  };
}

/** @param {`EnvelopesStep${string}`} page */
function toEditStep(page) {
  return {
    name: page,
    params: {
      fingerprint: envelope.value?.fingerprint,
      isEditStep: true,
    },
  };
}

const {
  sendEnvelope,
  isSendingEnvelope,
  sendErrorEnvelope,
  resetSendErrorEnvelope,
} = useEnvelopeSend(route.params.fingerprint);

async function send() {
  try {
    await sendEnvelope({
      envelope_progressed_at: envelope.value?.steps_progress?.updated_at,
    });

    EventBus.$emit('renderGlobalMessage', {
      message: i18n.t('ui.envelopes.create.steps.summary.sendSuccess'),
      icon: 'check',
      type: 'success',
    });

    await router.push({
      name: 'EnvelopeStatus',
      params: { envelopeFingerprint: route.params.fingerprint },
    });
  } catch (error) {
    // Do nothing, errors will be displayed to the user
  }
}

const reload = () => {
  refetchEnvelopeProgress();
  resetSendErrorEnvelope();
};

const back = computed(() => ({
  action: () => router.push(prevRoute.value),
  disabled: isDisabled.value,
}));

const next = computed(() => ({
  action: () => send(),
  label: i18n.t('ui.envelopes.create.steps.summary.send'),
  disabled: isDisabled.value || (company.value.new_billing_enabled && !usage.value.usage_allowed),
  busy: isSendingEnvelope.value,
}));
</script>
